import React, { useContext, useEffect, useState } from "react";

import { ElementState, Preview } from "@creatomate/preview";
import { classnames, MillisecondsField, StringField } from "@culturehq/components";
import { MillisecondsFieldValue } from "@culturehq/components/dist/components/form/MillisecondsField";

import { Trans } from "app/utils";
import { useTemplateBuilder } from "components/admin/templates/context/TemplateBuilderContext";
import useElementStartEnd from "../hooks/useElementStartEnd";
import { VIDEO_TYPE } from "../utils/constants";
import { CreatomateContext } from "../contexts/VideoStudioContext";

interface ElementStartEndProps {
  activeElement: ElementState;
  preview: Preview;
}

const ElementStartEnd: React.FC<ElementStartEndProps> = ({
  activeElement, preview }) => {
  const { templateBuilderSelection, dynamicElements } = useTemplateBuilder();
  const { data: { mediaVersionHistory }, handleUpdateData } = useContext(CreatomateContext);
  const isTheOriginal = mediaVersionHistory?.length === 0;
  const elementType = activeElement.source.type;
  const [isStartValid, setIsStartValid] = useState(true);
  const [isEndValid, setIsEndValid] = useState(true);
  const autoDuration = dynamicElements.find(de => de.elementId === activeElement.source.id)?.type === "story_media";

  const {
    startsInMilliSeconds,
    endsInMilliSeconds,
    handleOnStartsAtChange,
    handleOnEndsAtChange
  } = useElementStartEnd(preview);

  const startTime = Math.floor(startsInMilliSeconds);
  const endTime = Math.floor(endsInMilliSeconds);

  const [startValue, setStartValue] = useState(startTime);
  const [endValue, setEndValue] = useState(endTime);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsStartValid(startTime === startValue);
    }, 300);

    return () => clearTimeout(timeout);
  }, [startTime, startValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsEndValid(endTime === endValue);
    }, 300);

    return () => clearTimeout(timeout);
  }, [endTime, endValue]);

  useEffect(() => {
    setStartValue(startTime);
    setEndValue(endTime);
  }, [endTime, startTime]);

  const handleStartAtChange = (value: MillisecondsFieldValue) => {
    const newStartTime = typeof value === "number" ? value : 0;
    setStartValue(newStartTime);
    if (elementType === VIDEO_TYPE && !isTheOriginal) handleUpdateData({ blockSubtitles: true });
    handleOnStartsAtChange(newStartTime);
  };

  const handleEndsAtChange = (value: MillisecondsFieldValue) => {
    if (typeof value !== "number") return;
    if (elementType === VIDEO_TYPE && !isTheOriginal) handleUpdateData({ blockSubtitles: true });
    setEndValue(value);
    handleOnEndsAtChange(value);
  };

  const handleDurationChange = (value: MillisecondsFieldValue) => {
    if (elementType === VIDEO_TYPE && !isTheOriginal) handleUpdateData({ blockSubtitles: true });
    if (value) {
      const newEndTime = value + startsInMilliSeconds;
      handleOnEndsAtChange(newEndTime);
    }
  };

  const displayDuration = templateBuilderSelection !== "none";

  const durationComponent = () => (
    <>
      {autoDuration && (
        <StringField
          className="chq-ffd-border"
          min={startsInMilliSeconds / 100}
          name="auto_duration"
          value="Auto"
          disabled
        >
          <Trans>Duration</Trans>
        </StringField>
      )}
      {!autoDuration && (
        <MillisecondsField
          className="chq-ffd-border"
          min={startsInMilliSeconds / 100}
          name="duration"
          onChange={handleDurationChange}
          value={(endsInMilliSeconds - startsInMilliSeconds)}
        >
          <Trans>Duration</Trans>
        </MillisecondsField>
      )}
    </>
  );

  return (
    <div className="ElementStartEnd">
      <div onBlur={() => { setStartValue(startTime); }}>
        <MillisecondsField
          className={classnames("chq-ffd-border", !isStartValid ? "chq-ffd-border__invalid" : "")}
          min={0}
          name="start_at"
          onChange={handleStartAtChange}
          value={startValue}
        >
          <Trans>Starts at</Trans>
        </MillisecondsField>
      </div>

      <div onBlur={() => { setEndValue(endTime); }}>
        <MillisecondsField
          className={classnames("chq-ffd-border", !isEndValid ? "chq-ffd-border__invalid" : "")}
          min={0}
          name="end_at"
          onChange={handleEndsAtChange}
          value={endValue}
        >
          <Trans>Ends at</Trans>
        </MillisecondsField>
      </div>

      {displayDuration && durationComponent()}
    </div>
  );
};

export default ElementStartEnd;
